// DiscDiamondWithBase.jsx
import React, { useMemo } from "react";
import { Box } from "@mui/material";

/**
 * Mapeo de todas las zonas DISC a sus coordenadas en el SVG
 * ViewBox del SVG: 896.14 x 896.14
 */
const DISC_ZONES = {
  // Esquinas principales (1 letra)
  'D': { x: 749.99, y: 137.38 },
  'I': { x: 774.77, y: 809.93 },
  'S': { x: 85.16, y: 809.93 },
  'C': { x: 80.25, y: 137.38 },

  // Combinaciones de 2 letras
  'DI': { x: 754.9, y: 303.55 },
  'DC': { x: 563.45, y: 122.76 },
  'DS': { x: 516.98, y: 373.81 },
  'ID': { x: 754.9, y: 599.43 },
  'IS': { x: 563.45, y: 792.74 },
  'IC': { x: 516.98, y: 549.39 },
  'SI': { x: 280.96, y: 792.74 },
  'SD': { x: 341.62, y: 549.39 },
  'SC': { x: 85.16, y: 599.43 },
  'CS': { x: 85.16, y: 303.55 },
  'CD': { x: 280.96, y: 122.76 },
  'CI': { x: 341.62, y: 373.81 },

  // Combinaciones de 3 letras
  'DIS': { x: 670.79, y: 414.99 },
  'DIC': { x: 626.29, y: 316.69 },
  'DCS': { x: 474.51, y: 208.92 },
  'DCI': { x: 555.91, y: 264.39 },
  'DSI': { x: 563.45, y: 435.63 },
  'DSC': { x: 452.45, y: 322.17 },
  'IDS': { x: 626.29, y: 599.43 },
  'ICD': { x: 563.45, y: 484.37 },
  'ISC': { x: 474.51, y: 712.1 },
  'ISD': { x: 555.91, y: 661.74 },
  'ICS': { x: 462.45, y: 605.25 },
  'IDC': { x: 670.79, y: 505.29 },
  'SID': { x: 367.12, y: 712.1 },
  'SDI': { x: 391.07, y: 605.25 },
  'SIC': { x: 271.96, y: 661.74 },
  'SCI': { x: 213.03, y: 599.43 },
  'SDC': { x: 280.96, y: 484.37 },
  'SCD': { x: 165.96, y: 505.29 },
  'CID': { x: 393.07, y: 322.17 },
  'CDI': { x: 367.12, y: 208.92 },
  'CIS': { x: 280.96, y: 427.38 },
  'CSI': { x: 167.96, y: 414.99 },
  'CDS': { x: 271.96, y: 264.39 },
  'CSD': { x: 213.03, y: 316.69 },
};

/**
 * Determina el código DISC (ej: "DIS", "DI") basado en los puntajes
 * @param {Object} disc - {D, I, S, C} con valores numéricos
 * @returns {string} - Código DISC (ej: "DIS", "DI", "D")
 */
function getDiscCode(disc) {
  if (!disc) return null;

  const { D = 0, I = 0, S = 0, C = 0 } = disc;

  // Crear array de rasgos ordenados por puntaje (mayor a menor)
  const traits = [
    { letter: 'D', score: D },
    { letter: 'I', score: I },
    { letter: 'S', score: S },
    { letter: 'C', score: C }
  ].sort((a, b) => b.score - a.score);

  // Si los 2 primeros suman 75% o más, usar 2 letras
  const top2Sum = traits[0].score + traits[1].score;

  if (top2Sum >= 75) {
    return traits[0].letter + traits[1].letter;
  } else {
    // Si no, usar 3 letras
    return traits[0].letter + traits[1].letter + traits[2].letter;
  }
}

/**
 * Obtiene las coordenadas para un perfil DISC
 * @param {Object} disc - {D, I, S, C}
 * @returns {Object} - {x, y} coordenadas en el SVG
 */
function getDiscPosition(disc) {
  const code = getDiscCode(disc);
  const position = DISC_ZONES[code];

  if (!position) {
    console.warn(`No se encontró posición para el código DISC: ${code}`);
    return { x: 448.07, y: 448.07 }; // Centro por defecto
  }

  return position;
}

/**
 * Componente para mostrar el diamante DISC con perfiles natural y adaptado
 * 
 * Props:
 * - BaseSvg: componente React del SVG base (via SVGR)
 * - natural: {D, I, S, C} - perfil natural
 * - adapted: {D, I, S, C} - perfil adaptado
 */
export default function DiscDiamondWithBase({
  BaseSvg,
  natural,
  adapted,
}) {
  const VIEWBOX = 896.14;

  // Calcular posiciones basadas en códigos DISC
  const pN = useMemo(() => {
    const pos = getDiscPosition(natural);
    return { cx: pos.x, cy: pos.y };
  }, [natural]);

  const pA = useMemo(() => {
    const pos = getDiscPosition(adapted);
    return { cx: pos.x, cy: pos.y };
  }, [adapted]);

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: 520, mx: "auto" }}>
      {/* SVG base */}
      <Box sx={{ width: "100%", lineHeight: 0 }}>
        <BaseSvg width="100%" height="100%" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} />
      </Box>

      {/* Overlay dinámico */}
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        width="100%"
        height="100%"
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <defs>
          <marker id="discArrow" markerWidth="12" markerHeight="12" refX="7" refY="3.5" orient="auto">
            <path d="M0,0 L0,7 L7,3.5 z" fill="#111827" />
          </marker>
        </defs>

        {/* Punto natural (azul) */}
        <circle cx={pN.cx} cy={pN.cy} r={14} fill="#3b82f6" />

        {/* Flecha Natural -> Adaptado */}
        <line
          x1={pN.cx}
          y1={pN.cy}
          x2={pA.cx}
          y2={pA.cy}
          stroke="#111827"
          strokeWidth="5"
          markerEnd="url(#discArrow)"
        />

        {/* Punto adaptado (negro) */}
        <circle cx={pA.cx} cy={pA.cy} r={10} fill="#111827" />
      </svg>
    </Box>
  );
}